import { useState } from "react";
import { Checkbox, Chip, ListCell, Radio, TextField } from "@/components/ui";
import type { Menu, OptionGroup } from "@/lib/api/types";

/** customMenu 가 비어 있지 않으면 메뉴판 밖의 자유 입력 제출이다(menuId 는 null). */
export type MenuPickerValue = { menuId: string | null; choiceIds: string[]; customMenu: string };

export function MenuPicker({
  menus,
  value,
  onChange,
}: {
  menus: Menu[];
  value: MenuPickerValue;
  onChange: (value: MenuPickerValue) => void;
}) {
  const selectedMenu = menus.find((m) => m.id === value.menuId) ?? null;
  // 자유 입력 줄을 골랐지만 아직 아무 것도 안 적은 상태를 기억해야 폼이 닫히지 않는다.
  const [customOpen, setCustomOpen] = useState(value.customMenu.length > 0);
  const customSelected = value.menuId === null && (customOpen || value.customMenu.length > 0);

  function selectMenu(menu: Menu) {
    setCustomOpen(false);
    if (menu.id === value.menuId) return;
    // 다른 메뉴로 바꾸면 choiceIds 를 비운다 — 남겨두면 다른 메뉴의 옵션 id 로
    // 서버가 400 을 준다. 자유 입력도 같이 비운다(서버가 둘 다 오면 거절한다).
    onChange({ menuId: menu.id, choiceIds: [], customMenu: "" });
  }

  function selectCustom() {
    setCustomOpen(true);
    onChange({ menuId: null, choiceIds: [], customMenu: value.customMenu });
  }

  function pickSingle(group: OptionGroup, choiceId: string) {
    const groupChoiceIds = new Set(group.choices.map((c) => c.id));
    const withoutGroup = value.choiceIds.filter((id) => !groupChoiceIds.has(id));
    onChange({ ...value, choiceIds: [...withoutGroup, choiceId] });
  }

  function toggleMulti(choiceId: string) {
    const has = value.choiceIds.includes(choiceId);
    onChange({
      ...value,
      choiceIds: has
        ? value.choiceIds.filter((id) => id !== choiceId)
        : [...value.choiceIds, choiceId],
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <ul>
        {menus.map((menu) => (
          <li key={menu.id}>
            <ListCell
              title={menu.name}
              description={`${menu.price.toLocaleString("ko-KR")}원`}
              trailing={
                value.menuId === menu.id ? (
                  <Chip active size="small">
                    선택됨
                  </Chip>
                ) : undefined
              }
              onClick={() => selectMenu(menu)}
            />
          </li>
        ))}
        <li>
          <ListCell
            title="직접 적기"
            description="메뉴나 옵션이 목록에 없으면 그대로 적어 주세요"
            trailing={
              customSelected ? (
                <Chip active size="small">
                  선택됨
                </Chip>
              ) : undefined
            }
            onClick={selectCustom}
          />
        </li>
      </ul>

      {customSelected && (
        <div className="mt-2">
          <TextField
            label="직접 적기"
            placeholder="예: 딸기라떼 아이스, 시럽 빼고"
            maxLength={60}
            value={value.customMenu}
            onChange={(e) => onChange({ menuId: null, choiceIds: [], customMenu: e.target.value })}
          />
          <p className="mt-1 text-caption-1 text-label-assistive">
            가격은 메뉴판에 없어서 정산 금액에는 안 잡혀요.
          </p>
        </div>
      )}

      {selectedMenu && selectedMenu.option_groups.length > 0 && (
        <div className="mt-2 flex flex-col gap-4">
          {selectedMenu.option_groups.map((group) => (
            <div key={group.id}>
              <p className="text-label-1 font-semibold text-label-normal">{group.name}</p>
              <div className="mt-2 flex flex-col gap-2">
                {group.choices.map((choice) =>
                  group.type === "single" ? (
                    <Radio
                      key={choice.id}
                      label={choice.name}
                      checked={value.choiceIds.includes(choice.id)}
                      onChange={() => pickSingle(group, choice.id)}
                    />
                  ) : (
                    <Checkbox
                      key={choice.id}
                      label={choice.name}
                      checked={value.choiceIds.includes(choice.id)}
                      onChange={() => toggleMulti(choice.id)}
                    />
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
